import { type Dispatch, type SetStateAction, useEffect, useMemo, useRef, useState } from 'react'

import { type Edge, useNodesInitialized, useReactFlow } from '@xyflow/react'

import { SIZES } from './types'
import {
  buildNodeMap,
  buildParentMap,
  expandAncestorsAndSelectNode,
  getAncestors,
} from './treeUtils'
import { type CustomNode } from './types'

const SIDEPANEL_WIDTH = 600
const SIDEPANEL_VIEWPORT_OFFSET_RATIO = 0.35
const SELECTION_VIEWPORT_VERTICAL_OFFSET = 120

interface UseNodeSelectionFromRouteProps {
  nodeId?: string
  nodes: CustomNode[]
  setNodes: Dispatch<SetStateAction<CustomNode[]>>
  edges: Edge[]
}

const useNodeSelectionFromRoute = ({
  nodeId,
  nodes,
  setNodes,
  edges,
}: UseNodeSelectionFromRouteProps) => {
  const { getNode, getZoom, setCenter, viewportInitialized } = useReactFlow()
  const nodesInitialized = useNodesInitialized()
  const [pendingNodeId, setPendingNodeId] = useState<string>()
  const lastHandledNodeIdRef = useRef<string>()

  const nodeMap = useMemo(() => buildNodeMap(nodes), [nodes])
  const parentMap = useMemo(() => buildParentMap(edges), [edges])
  const routeTargetNode = useMemo(
    () => nodes.find((node) => node.id === nodeId || node.data.node_uuid === nodeId),
    [nodeId, nodes]
  )
  const routeSelectionNeedsSync = useMemo(() => {
    if (!routeTargetNode) {
      return false
    }

    if (!routeTargetNode.selected) {
      return true
    }

    const ancestors = getAncestors(routeTargetNode.id, parentMap)

    return ancestors.some((ancestorId) => {
      const ancestorNode = nodeMap.get(ancestorId)

      return ancestorNode ? !ancestorNode.data.expanded : false
    })
  }, [nodeMap, parentMap, routeTargetNode])

  useEffect(() => {
    if (nodeId) {
      if (!routeTargetNode) {
        setPendingNodeId(undefined)
        return
      }

      if (routeSelectionNeedsSync) {
        setNodes((nds) => expandAncestorsAndSelectNode(nds, routeTargetNode.id, parentMap))
        setPendingNodeId(routeTargetNode.id)
        lastHandledNodeIdRef.current = routeTargetNode.id
        return
      }

      if (lastHandledNodeIdRef.current !== routeTargetNode.id) {
        setPendingNodeId(routeTargetNode.id)
        lastHandledNodeIdRef.current = routeTargetNode.id
      }
    } else {
      setPendingNodeId(undefined)
      lastHandledNodeIdRef.current = undefined

      // Deselect all nodes when there's no id param
      setNodes((nds) => {
        const hasSelectedNodes = nds.some((n) => n.selected)
        if (!hasSelectedNodes) {
          return nds
        }

        return nds.map((n) => (n.selected ? { ...n, selected: false } : n))
      })
    }
  }, [nodeId, parentMap, routeSelectionNeedsSync, routeTargetNode, setNodes])

  useEffect(() => {
    if (!pendingNodeId || !viewportInitialized || !nodesInitialized) {
      return
    }

    const selectedNode = getNode(pendingNodeId)
    if (!selectedNode) {
      setPendingNodeId(undefined)
      return
    }

    const zoom = Math.min(getZoom() || 1, 1)
    const nodeCenterX = selectedNode.position.x + SIZES.NODE_WIDTH / 2
    const nodeCenterY = selectedNode.position.y + SIZES.NODE_HEIGHT / 2
    const flowOffsetX = (SIDEPANEL_WIDTH * SIDEPANEL_VIEWPORT_OFFSET_RATIO) / zoom
    const flowOffsetY = SELECTION_VIEWPORT_VERTICAL_OFFSET / zoom

    void setCenter(nodeCenterX + flowOffsetX, nodeCenterY + flowOffsetY, {
      duration: 300,
      zoom,
    })

    setPendingNodeId(undefined)
  }, [getNode, getZoom, nodesInitialized, pendingNodeId, setCenter, viewportInitialized])
}

export default useNodeSelectionFromRoute
